import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import { useAuth } from '../hooks/useAuth';
import { bookingService } from '../services/bookingService';
import { getUserPhone } from '../services/userService';
import { formatDate, formatTime } from '../utils/dateUtils';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import { format, addDays } from 'date-fns';

/**
 * My Bookings page to view user's appointments
 * Fetches bookings by user's phone number across all customer IDs
 */
const MyBookingsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [tabValue, setTabValue] = useState(0);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [pastBookings, setPastBookings] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [userPhone, setUserPhone] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelError, setCancelError] = useState(null);

  useEffect(() => {
    const loadUserPhone = async () => {
      if (!user) return;
      
      try {
        const phone = await getUserPhone(user.email);
        setUserPhone(phone);
      } catch (error) {
        console.error('Error loading user phone:', error);
      }
    };
    
    loadUserPhone();
  }, [user]);

  const fetchBookings = async () => {
    if (!user || !userPhone) {
      setFetchLoading(false);
      return;
    }

    setFetchLoading(true);
    setFetchError(null);

    try {
      // Fetch events using multi-business lookup (will query booking_businesses array)
      const normalizedPhone = normalizePhoneNumber(userPhone);
      
      // Fetch events from 30 days ago to 90 days in the future
      const now = new Date();
      const startDate = format(addDays(now, -30), 'yyyy-MM-dd');
      const endDate = format(addDays(now, 90), 'yyyy-MM-dd');
      
      // Use any customer_id - the backend will use booking_businesses array if available
      const result = await bookingService.getUserBookings('fluente-demo', normalizedPhone, {
        start_date: startDate,
        end_date: endDate
      });
      
      const allUpcoming = [];
      const allPast = [];

      if (result?.customers) {
        // Parse events from nested structure: {customerId: {business_name, calendars: {calendarName: {data}}}}
        Object.entries(result.customers).forEach(([customerId, customerInfo]) => {
          const businessName = customerInfo.business_name || customerId;
          const calendars = customerInfo.calendars || {};
          
          Object.entries(calendars).forEach(([calendarName, calendarData]) => {
            const events = calendarData.calendar_events || [];
            const actualCalendarName = calendarData.calendar_config?.name || calendarName;
            
            events.forEach(event => {
              if (event.is_customer_meeting) {
                // Use end time to determine if event is past
                const eventEnd = new Date(event.end);
                const bookingData = {
                  id: event.event_id,
                  customerId: customerId,
                  calendarName: actualCalendarName,
                  businessName: businessName,
                  summary: event.summary,
                  start: event.start,
                  end: event.end,
                  phone: event.phone,
                  email: event.email,
                  notes: event.notes,
                  description: event.description,
                  meetingType: event.meeting_type || 'N/A',
                };
                    
                // Check if event has ended (not just started)
                if (eventEnd >= now) {
                  allUpcoming.push(bookingData);
                } else {
                  allPast.push(bookingData);
                }
              }
            });
          });
        });
      }
      
      // Sort by date
      allUpcoming.sort((a, b) => new Date(a.start) - new Date(b.start));
      allPast.sort((a, b) => new Date(b.start) - new Date(a.start));
      
      setUpcomingBookings(allUpcoming);
      setPastBookings(allPast);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [user, userPhone]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCancelBookingClick = (booking) => {
    setBookingToCancel(booking);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!bookingToCancel) return;
    
    setCancelError(null);
    
    try {
      await bookingService.cancelBooking(
        bookingToCancel.customerId,
        bookingToCancel.calendarName,
        bookingToCancel.id
      );
      
      // Close dialog first
      setCancelDialogOpen(false);
      setBookingToCancel(null);
      
      // Refresh bookings from server
      await fetchBookings();
    } catch (err) {
      console.error('Error cancelling booking:', err);
      // Extract error message from response
      const errorMessage = err.response?.data?.error || err.message || 'Failed to cancel booking';
      setCancelError(errorMessage);
      // Don't close dialog on error - show error message instead
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setBookingToCancel(null);
    setCancelError(null);
  };

  const handleRebookBooking = (booking) => {
    // Navigate to booking page with full rebook context including customer data
    navigate(`/book/${booking.customerId}`, {
      state: {
        customerId: booking.customerId,
        rebookData: {
          eventId: booking.id,
          calendarName: booking.calendarName,
          originalStart: booking.start,
          originalEnd: booking.end,
          customerName: booking.summary,
          customerEmail: booking.email,
          customerPhone: booking.phone,
          notes: booking.notes
        }
      }
    });
  };

  const getStatusChip = (status) => {
    const statusColors = {
      confirmed: 'success',
      pending: 'warning',
      cancelled: 'error',
      completed: 'default',
    };
    return (
      <Chip 
        label={t(`myBookings.status.${status}`) || status} 
        size="small" 
        color={statusColors[status] || 'default'}
      />
    );
  };

  const renderBookingList = (bookings, showCancel = false) => {
    if (bookings.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            {tabValue === 0 ? t('myBookings.noUpcoming') : t('myBookings.noPast')}
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {bookings.map((booking, index) => {
          const startTime = booking.start ? new Date(booking.start.dateTime || booking.start) : null;
          const endTime = booking.end ? new Date(booking.end.dateTime || booking.end) : null;

          return (
            <Box key={booking.id || index}>
              <ListItem 
                sx={{ 
                  py: 2, 
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.light',
                  bgcolor: 'background.paper',
                  mb: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main',
                  },
                  ...((!showCancel) && {
                    opacity: 0.85,
                  }),
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {/* Business Name */}
                      {booking.businessName && (
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600,
                            color: 'primary.main',
                            fontSize: { xs: '1rem', sm: '1.125rem' }
                          }}
                        >
                          {booking.businessName}
                        </Typography>
                      )}
                      
                      {/* Meeting Type */}
                      {booking.meetingType && booking.meetingType !== 'N/A' && (
                        <Chip
                          label={booking.meetingType}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            fontWeight: 500,
                            alignSelf: 'flex-start',
                          }}
                        />
                      )}
                      
                      {/* Calendar Name */}
                      {booking.calendarName && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.813rem', sm: '0.875rem' }
                          }}
                        >
                          📅 {booking.calendarName}
                        </Typography>
                      )}
                      
                      {booking.status && getStatusChip(booking.status)}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {startTime && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: 'text.primary',
                            textTransform: 'capitalize',
                            fontSize: { xs: '0.813rem', sm: '0.875rem' }
                          }}
                        >
                          🗓️ {formatDate(startTime, 'EEEE, MMMM d, yyyy')}
                        </Typography>
                      )}
                      {startTime && endTime && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mt: 0.5,
                            fontSize: { xs: '0.813rem', sm: '0.875rem' }
                          }}
                        >
                          🕐 {formatTime(startTime, 'HH:mm')} - {formatTime(endTime, 'HH:mm')}
                        </Typography>
                      )}
                      {booking.location && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mt: 0.5,
                            fontSize: { xs: '0.813rem', sm: '0.875rem' }
                          }}
                        >
                          📍 {booking.location}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                {showCancel && booking.customerId && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%', mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => handleRebookBooking(booking)}
                      disabled={fetchLoading}
                      sx={{ 
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                        minWidth: { sm: 120 }
                      }}
                    >
                      {t('myBookings.rebookAppointment')}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleCancelBookingClick(booking)}
                      disabled={fetchLoading}
                      sx={{ 
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                        minWidth: { sm: 120 }
                      }}
                    >
                      {t('myBookings.cancelBooking')}
                    </Button>
                  </Box>
                )}
              </ListItem>
              {index < bookings.length - 1 && <Box sx={{ mb: 0 }} />}
            </Box>
          );
        })}
      </List>
    );
  };

  if (fetchLoading) {
    return <LoadingSpinner message={t('common.loading')} />;
  }

  if (fetchError) {
    return <ErrorDisplay message={fetchError} />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          mb: { xs: 2, sm: 3 }
        }}
      >
        {t('myBookings.title')}
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: '0.813rem', sm: '0.875rem' },
              minHeight: { xs: 48, sm: 64 },
            }
          }}
        >
          <Tab label={`${t('myBookings.upcoming')} (${upcomingBookings.length})`} />
          <Tab label={`${t('myBookings.past')} (${pastBookings.length})`} />
        </Tabs>

        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          {tabValue === 0 && renderBookingList(upcomingBookings, true)}
          {tabValue === 1 && renderBookingList(pastBookings, false)}
        </Box>
      </Paper>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          {t('myBookings.confirmCancelTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            {t('myBookings.confirmCancelMessage')}
          </DialogContentText>
          {cancelError && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1, color: 'error.contrastText' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {cancelError}
              </Typography>
            </Box>
          )}
          {bookingToCancel && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {bookingToCancel.calendarName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(new Date(bookingToCancel.start), 'EEEE, MMMM d, yyyy')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatTime(new Date(bookingToCancel.start), 'HH:mm')} - {formatTime(new Date(bookingToCancel.end), 'HH:mm')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} color="primary">
            {t('myBookings.no')}
          </Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained" autoFocus>
            {t('myBookings.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyBookingsPage;
